import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Copy, Check, Loader2, Clock, CheckCircle2, XCircle, ExternalLink, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import QRCode from "react-qr-code";
import { getCryptoIconUrl, getCryptoIconFallback } from "@/lib/cryptoIcons";

const allCryptos = [
  { currency: "USDT", name: "Tether" },
  { currency: "USDC", name: "USD Coin" },
  { currency: "TON", name: "Toncoin" },
  { currency: "SOL", name: "Solana" },
  { currency: "TRX", name: "TRON" },
  { currency: "BTC", name: "Bitcoin" },
  { currency: "ETH", name: "Ethereum" },
];

const TERMINAL = ["credited", "expired", "canceled", "rejected", "failed"];
const CYRILLIC_RE = /^[А-Яа-яЁё]+$/;
const PHONE_RE = /^\+?\d{10,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fmtCrypto = (v, cur) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  const dec = cur === "BTC" || cur === "ETH" || n < 1 ? 6 : 2;
  return n.toFixed(dec).replace(/\.?0+$/, "");
};
const fmtRub = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2).replace(/\.?0+$/, "");
};
const fmtInt = (v) => {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("ru-RU");
};
const fmtClock = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};

const openPayment = (url) => {
  if (!url) return;
  try {
    if (window?.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(url);
      return;
    }
  } catch {
    /* fall through */
  }
  window.open(url, "_blank", "noopener");
};

const InlinePageHeader = ({ title, subtitle, onBack, showBack = true }) => (
  <div className="lp-bar lp-topbar sticky top-0 z-30 px-4 py-3 grid grid-cols-[2rem_1fr_2rem] items-center border-b border-[var(--lp-hair)]">
    {showBack ? (
      <button type="button" onClick={onBack} className="inline-flex h-8 w-8 items-center justify-center text-foreground" aria-label="Back">
        <ChevronLeft className="w-5 h-5" />
      </button>
    ) : (
      <span aria-hidden="true" className="h-8 w-8" />
    )}
    <div className="text-center">
      <h1 className="text-[17px] font-semibold text-foreground leading-tight">{title}</h1>
      {subtitle && <p className="text-xs text-muted-foreground leading-tight">{subtitle}</p>}
    </div>
    <span aria-hidden="true" className="h-8 w-8" />
  </div>
);

const Field = ({ label, value, onChange, placeholder, error, testid, type = "text" }) => (
  <div>
    <label className="text-sm text-muted-foreground mb-1 block">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "w-full bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-foreground outline-none border",
        error ? "border-red-400" : "border-transparent"
      )}
      data-testid={testid}
    />
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const RubTopUpPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api, haptic, isTelegramWebApp, setBackHandler, clearBackHandler } = useApp();
  const showInlineBack = !isTelegramWebApp;

  const [config, setConfig] = useState(null);
  const [currency, setCurrency] = useState("USDT");
  const [mode, setMode] = useState("rub");
  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [step, setStep] = useState("amount"); // 'amount' | 'customer'
  const [pii, setPii] = useState({ first_name: "", second_name: "", phone: "", email: "" });
  const [creating, setCreating] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [anchorRate, setAnchorRate] = useState(null); // курс ₽/ед. для пересчёта лимитов в крипту

  const cryptoOptions = useMemo(() => {
    const sup = config?.supported_currencies;
    if (Array.isArray(sup) && sup.length) {
      return allCryptos.filter((c) => sup.includes(c.currency));
    }
    return allCryptos;
  }, [config]);

  const isRedirect = config?.payment_kind === "redirect";
  const requiresCustomer = !!config?.requires_customer;
  const singleCurrency = cryptoOptions.length === 1;
  const soleCurrency = cryptoOptions[0]?.currency || currency;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/rub-topup/config");
        if (cancelled) return;
        setConfig(res?.data || null);
        const sup = res?.data?.supported_currencies;
        if (Array.isArray(sup) && sup.length && !sup.includes("USDT")) setCurrency(sup[0]);
      } catch {
        // Не удалось получить конфиг рельса — считаем фичу недоступной, а не
        // подставляем stub-подобную форму. Лучше «недоступно», чем фейковый экран.
        if (!cancelled) setConfig({ enabled: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const handleBack = useCallback(() => {
    haptic?.("light");
    if (invoice) return setInvoice(null);
    if (step === "customer") return setStep("amount");
    navigate(-1);
  }, [haptic, invoice, step, navigate]);

  useEffect(() => {
    if (!isTelegramWebApp) return;
    setBackHandler(handleBack);
    return () => clearBackHandler();
  }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

  // Котировка с дебаунсом
  useEffect(() => {
    const val = Number(amount);
    if (!Number.isFinite(val) || val <= 0) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoting(true);
    const id = setTimeout(async () => {
      try {
        const body = { target_currency: currency };
        if (mode === "rub") body.rub_amount = val;
        else body.crypto_amount = val;
        const res = await api.post("/rub-topup/quote", body);
        if (!cancelled) setQuote(res?.data || null);
      } catch {
        if (!cancelled) setQuote(null);
      } finally {
        if (!cancelled) setQuoting(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [api, currency, mode, amount]);

  // Якорный курс: одна котировка на минимум, чтобы знать границы в крипте до ввода.
  useEffect(() => {
    const min = Number(config?.min_rub);
    if (!config || !Number.isFinite(min) || min <= 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post("/rub-topup/quote", { target_currency: currency, rub_amount: min });
        const r = Number(res?.data?.rate_rub_per_unit);
        if (!cancelled && Number.isFinite(r) && r > 0) setAnchorRate(r);
      } catch {
        /* оставим null — до ввода границы в крипте просто не покажем */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, currency, config]);

  const piiErrors = useMemo(() => {
    const e = {};
    if (pii.first_name && !CYRILLIC_RE.test(pii.first_name.trim())) e.first_name = t("rubtopup.name_cyrillic_error");
    if (pii.second_name && !CYRILLIC_RE.test(pii.second_name.trim())) e.second_name = t("rubtopup.name_cyrillic_error");
    if (pii.phone && !PHONE_RE.test(pii.phone.trim())) e.phone = t("rubtopup.phone_error");
    if (pii.email && !EMAIL_RE.test(pii.email.trim())) e.email = t("rubtopup.email_error");
    return e;
  }, [pii, t]);

  const piiComplete =
    pii.first_name && pii.second_name && pii.phone && pii.email && Object.keys(piiErrors).length === 0;

  const createInvoice = async () => {
    const val = Number(amount);
    if (!Number.isFinite(val) || val <= 0) return;
    haptic?.("medium");
    setCreating(true);
    try {
      const body = { target_currency: currency };
      if (mode === "rub") body.rub_amount = val;
      else body.crypto_amount = val;
      if (requiresCustomer) {
        body.user_email = pii.email.trim();
        body.customer = {
          first_name: pii.first_name.trim(),
          second_name: pii.second_name.trim(),
          phone: pii.phone.trim(),
        };
      }
      const res = await api.post("/rub-topup/invoice", body);
      const data = res?.data || null;
      setInvoice(data);
      if (data?.payment_url) openPayment(data.payment_url);
    } catch (err) {
      // Разбираем ТОЛЬКО коды, для которых у нас есть свой текст. Остальные
      // detail'ы написаны для интегратора и на английском («Payment rail not
      // configured», «Unsupported currency»): показывать их человеку — значит
      // рассказывать ему про наши рельсы вместо того, что ему делать.
      const detail = err?.response?.data?.detail;
      let msg = t("rubtopup.create_error");
      if (typeof detail === "string") {
        if (detail.startsWith("amount_below_min"))
          msg = t("rubtopup.below_min", { amount: fmtInt(detail.split(":")[1]) });
        else if (detail.startsWith("amount_above_max"))
          msg = t("rubtopup.above_max", { amount: fmtInt(detail.split(":")[1]) });
      }
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const onPrimary = () => {
    if (requiresCustomer && step === "amount") {
      if (!quote) return;
      setStep("customer");
      return;
    }
    createInvoice();
  };

  // Поллинг статуса
  useEffect(() => {
    if (!invoice?.order_id) return;
    if (TERMINAL.includes(invoice.status)) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.get(`/rub-topup/status/${encodeURIComponent(invoice.order_id)}`);
        if (cancelled || !res?.data) return;
        const prev = invoice.status;
        setInvoice((p) => (p ? { ...p, ...res.data } : res.data));
        if (res.data.status === "credited" && prev !== "credited") {
          haptic?.("success");
          toast.success(
            t("rubtopup.credited_toast", {
              amount: fmtCrypto(res.data.credited_amount || res.data.receive_amount, currency),
              currency,
            })
          );
        }
      } catch {
        /* повторит следующий тик */
      }
    };
    const id = setInterval(tick, 4000);
    tick();
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [api, invoice?.order_id, invoice?.status, currency, haptic, t]);

  useEffect(() => {
    if (!invoice) return;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [invoice]);

  const secondsLeft = useMemo(() => {
    if (!invoice?.expires_at) return null;
    const exp = Number(invoice.expires_at);
    if (!Number.isFinite(exp)) return null;
    return Math.max(0, exp - now);
  }, [invoice, now]);

  const minRub = Number(config?.min_rub) || 0;
  const maxRub = Number(config?.max_rub) || 0;
  const rate = Number(quote?.rate_rub_per_unit) || Number(anchorRate) || 0;
  const cryptoPresetCur = currency === "USDT" || currency === "USDC";
  // Границы в валюте ввода. Крипто-границы округляем ВНУТРЬ окна: мин вверх, макс вниз,
  // чтобы пресет/ввод по этим числам не выпал за рублёвый лимит провайдера.
  const minUnit = mode === "rub" ? minRub : rate ? Math.ceil((minRub / rate) * 100) / 100 : 0;
  const maxUnit = mode === "rub" ? maxRub : rate ? Math.floor((maxRub / rate) * 100) / 100 : 0;
  const unitLabel = mode === "rub" ? "₽" : currency;
  const minDisp = mode === "rub" ? fmtInt(minRub) : fmtCrypto(minUnit, currency);
  const maxDisp = mode === "rub" ? fmtInt(maxRub) : fmtCrypto(maxUnit, currency);

  // Валидация — всегда по рублёвому эквиваленту (у провайдера лимит в ₽, это источник истины).
  const rubValue = mode === "rub" ? Number(amount) : quote ? Number(quote.rub_amount) : null;
  const rangeError = useMemo(() => {
    if (rubValue == null || !Number.isFinite(rubValue) || rubValue <= 0) return null;
    if (minRub && rubValue < minRub) return { key: "below_min", amount: minDisp, unit: unitLabel };
    if (maxRub && rubValue > maxRub) return { key: "above_max", amount: maxDisp, unit: unitLabel };
    return null;
  }, [rubValue, minRub, maxRub, minDisp, maxDisp, unitLabel]);

  const presets = useMemo(() => {
    if (mode === "rub") {
      if (!minRub && !maxRub) return [];
      return [5000, 10000, 25000, 50000, 100000]
        .filter((v) => (!minRub || v >= minRub) && (!maxRub || v <= maxRub))
        .map((v) => ({ value: v, label: `${fmtInt(v)} ₽` }));
    }
    if (!cryptoPresetCur || !minUnit || !maxUnit) return [];
    return [100, 250, 500, 1000, 2500, 5000]
      .filter((v) => v >= minUnit && v <= maxUnit)
      .map((v) => ({ value: v, label: `${fmtInt(v)} ${currency}` }));
  }, [mode, minRub, maxRub, minUnit, maxUnit, cryptoPresetCur, currency]);

  const handleCopy = async (text) => {
    if (!text) return;
    haptic?.("light");
    try {
      await navigator.clipboard.writeText(String(text));
      setCopied(true);
      toast.success(t("rubtopup.copied"));
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t("rubtopup.copy_error"));
    }
  };

  const handleCancel = async () => {
    if (!invoice?.order_id) return;
    haptic?.("light");
    try {
      await api.post(`/rub-topup/${encodeURIComponent(invoice.order_id)}/cancel`);
      setInvoice((p) => (p ? { ...p, status: "canceled" } : p));
    } catch {
      toast.error(t("rubtopup.cancel_error"));
    }
  };

  // ------------------------------------------------------------------ Инвойс
  if (invoice) {
    const status = invoice.status;
    const isCredited = status === "credited";
    const isDead = ["expired", "canceled", "rejected", "failed"].includes(status);
    const redirect = !!invoice.payment_url;
    return (
      <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col" data-testid="rubtopup-invoice-page">
        <InlinePageHeader title={t("rubtopup.title")} subtitle={singleCurrency ? soleCurrency : undefined} onBack={handleBack} showBack={showInlineBack} />
        <div className="flex-1 overflow-y-auto overscroll-contain pb-[calc(5rem+var(--lp-bottom))]" style={{ WebkitOverflowScrolling: "touch" }}>
          <div className="p-6 space-y-5">
            {isCredited && (
              <div className="flex flex-col items-center gap-2 py-6" data-testid="rubtopup-credited">
                <CheckCircle2 className="w-14 h-14 text-green-500" />
                <p className="text-lg font-semibold">{t("rubtopup.credited_title")}</p>
                <p className="text-sm text-muted-foreground">
                  +{fmtCrypto(invoice.credited_amount || invoice.receive_amount, currency)} {currency}
                </p>
                <button onClick={() => navigate(-1)} className="mt-4 w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold">
                  {t("rubtopup.done")}
                </button>
              </div>
            )}

            {isDead && (
              <div className="flex flex-col items-center gap-2 py-6" data-testid="rubtopup-dead">
                <XCircle className="w-14 h-14 text-gray-400" />
                <p className="text-lg font-semibold">{t(`rubtopup.status_${status}`)}</p>
                <button onClick={() => { setInvoice(null); setStep("amount"); }} className="mt-4 w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold">
                  {t("rubtopup.new_invoice")}
                </button>
              </div>
            )}

            {!isCredited && !isDead && (
              <>
                <div className="flex items-center justify-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <Clock className="w-4 h-4" />
                  {secondsLeft != null ? t("rubtopup.expires_in", { time: fmtClock(secondsLeft) }) : t("rubtopup.waiting")}
                </div>

                {redirect ? (
                  <div className="space-y-4">
                    <p className="text-center text-sm text-muted-foreground">{t("rubtopup.pay_on_page")}</p>
                    <button
                      onClick={() => openPayment(invoice.payment_url)}
                      className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2"
                      data-testid="rubtopup-open-payment"
                    >
                      <ExternalLink className="w-5 h-5" />
                      {t("rubtopup.open_payment")}
                    </button>
                    <p className="text-xs text-gray-400 text-center">{t("rubtopup.after_pay_hint")}</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-center">
                      <div className="bg-white p-4 rounded-2xl shadow-sm">
                        <QRCode value={invoice.qr_payload || invoice.order_id} size={200} level="M" data-testid="rubtopup-qr" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm text-muted-foreground">{t("rubtopup.pay_exactly")}</label>
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 flex items-center gap-3">
                        <p className="text-xl font-semibold flex-1 text-foreground" data-testid="rubtopup-amount">
                          {fmtRub(invoice.rub_amount_exact)} ₽
                        </p>
                        <button onClick={() => handleCopy(invoice.rub_amount_exact)} className={cn("p-2 rounded-lg transition-colors", copied ? "bg-green-100 text-green-600" : "bg-white hover:bg-gray-100 text-gray-900")} data-testid="rubtopup-copy">
                          {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <p className="text-xs text-muted-foreground text-center">
                  {t("rubtopup.will_receive", { amount: fmtCrypto(invoice.receive_amount, currency), currency })}
                </p>

                {Array.isArray(invoice.requisites) && invoice.requisites.length > 0 && (
                  <div className="space-y-1 rounded-xl border border-[rgb(var(--lp-edge)/var(--lp-edge-a))] p-4">
                    {invoice.requisites.map((r, i) => (
                      <div key={i} className="flex justify-between gap-3 text-sm py-1">
                        <span className="text-muted-foreground">{r.label}</span>
                        <span className="text-right font-medium break-all">{r.value}</span>
                      </div>
                    ))}
                  </div>
                )}

                <button onClick={handleCancel} className="w-full py-3 rounded-xl border border-[rgb(var(--lp-edge)/var(--lp-edge-a))] text-muted-foreground font-medium" data-testid="rubtopup-cancel">
                  {t("rubtopup.cancel")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------- Рельс не настроен / недоступен
  if (config && config.enabled === false) {
    return (
      <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col safe-top" data-testid="rubtopup-unavailable-page">
        <InlinePageHeader title={t("rubtopup.title")} onBack={handleBack} showBack={showInlineBack} />
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center">
          <XCircle className="w-14 h-14 text-gray-400" />
          <p className="text-lg font-semibold text-foreground">{t("rubtopup.unavailable")}</p>
          <p className="text-sm text-gray-500 max-w-xs">{t("rubtopup.unavailable_desc")}</p>
          <button onClick={() => navigate(-1)} className="mt-4 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold">
            {t("rubtopup.done")}
          </button>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------ Шаг: ПДн
  if (step === "customer") {
    return (
      <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col" data-testid="rubtopup-customer-page">
        <InlinePageHeader title={t("rubtopup.payer_details")} onBack={handleBack} showBack={showInlineBack} />
        <div className="flex-1 overflow-y-auto overscroll-contain pb-[calc(5rem+var(--lp-bottom))]" style={{ WebkitOverflowScrolling: "touch" }}>
          <div className="p-6 space-y-4">
            <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-700/40 px-3 py-2.5">
              <p className="text-xs leading-snug text-amber-900 dark:text-amber-100">{t("rubtopup.payer_hint")}</p>
            </div>
            <Field label={t("rubtopup.first_name")} value={pii.first_name} onChange={(v) => setPii((p) => ({ ...p, first_name: v }))} placeholder="Иван" error={piiErrors.first_name} testid="pii-first-name" />
            <Field label={t("rubtopup.second_name")} value={pii.second_name} onChange={(v) => setPii((p) => ({ ...p, second_name: v }))} placeholder="Иванов" error={piiErrors.second_name} testid="pii-second-name" />
            <Field label={t("rubtopup.phone")} value={pii.phone} onChange={(v) => setPii((p) => ({ ...p, phone: v }))} placeholder="+79990000000" error={piiErrors.phone} testid="pii-phone" type="tel" />
            <Field label={t("rubtopup.email")} value={pii.email} onChange={(v) => setPii((p) => ({ ...p, email: v }))} placeholder="mail@example.com" error={piiErrors.email} testid="pii-email" type="email" />

            <button
              onClick={createInvoice}
              disabled={!piiComplete || creating}
              className={cn("w-full py-3.5 rounded-xl font-semibold transition-colors", piiComplete && !creating ? "bg-primary text-primary-foreground" : "bg-gray-200 dark:bg-gray-800 text-gray-400")}
              data-testid="rubtopup-pay"
            >
              {creating ? t("rubtopup.creating") : t("rubtopup.pay")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------ Шаг: сумма
  const limitHint = !(minRub && maxRub)
    ? ""
    : mode === "crypto" && minUnit && maxUnit
    ? t("rubtopup.limits_crypto", { min: minDisp, max: maxDisp, currency })
    : t("rubtopup.limits", { min: fmtInt(minRub), max: fmtInt(maxRub) });

  const canProceed = Number(amount) > 0 && !!quote && !quoting && !creating && !rangeError;

  return (
    <div className="h-[100dvh] min-h-[100dvh] bg-background flex flex-col" data-testid="rubtopup-form-page">
      <InlinePageHeader title={t("rubtopup.title")} subtitle={singleCurrency ? soleCurrency : undefined} onBack={handleBack} showBack={showInlineBack} />
      <div className="flex-1 overflow-y-auto overscroll-contain pb-[calc(5rem+var(--lp-bottom))]" style={{ WebkitOverflowScrolling: "touch" }}>
        <div className="p-6 space-y-6">
          {/* Валюта */}
          {singleCurrency ? (
            <div className="flex items-center gap-3 rounded-2xl bg-gray-50 dark:bg-gray-800/60 p-4" data-testid="rubtopup-currency-card">
              <img
                src={getCryptoIconUrl(soleCurrency)}
                alt={soleCurrency}
                className="w-11 h-11 rounded-full shrink-0"
                loading="lazy"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = getCryptoIconFallback(soleCurrency); }}
              />
              <div className="min-w-0">
                <p className="font-semibold text-foreground">{t("rubtopup.topup_currency_title", { currency: soleCurrency })}</p>
                <p className="text-sm text-muted-foreground leading-snug">{t("rubtopup.topup_currency_desc", { currency: soleCurrency })}</p>
              </div>
            </div>
          ) : (
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">{t("rubtopup.currency")}</label>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {cryptoOptions.map((c) => (
                  <button
                    key={c.currency}
                    type="button"
                    onClick={() => { haptic?.("light"); setCurrency(c.currency); }}
                    className={cn("flex items-center gap-2 px-3 py-2 rounded-xl border shrink-0 transition-colors", currency === c.currency ? "border-primary bg-primary/5" : "border-[rgb(var(--lp-edge)/var(--lp-edge-a))]")}
                    data-testid={`rubtopup-cur-${c.currency.toLowerCase()}`}
                  >
                    <img src={getCryptoIconUrl(c.currency)} alt={c.currency} className="w-6 h-6 rounded-full" loading="lazy" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = getCryptoIconFallback(c.currency); }} />
                    <span className="text-sm font-medium">{c.currency}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Переключатель режима */}
          <div className="flex rounded-xl bg-gray-100 dark:bg-gray-800 p-1" data-testid="rubtopup-mode">
            <button type="button" onClick={() => { haptic?.("light"); setMode("rub"); setAmount(""); }} className={cn("flex-1 py-2 rounded-lg text-sm font-medium transition-colors", mode === "rub" ? "bg-white dark:bg-gray-700 shadow-sm text-foreground" : "text-muted-foreground")}>
              {t("rubtopup.mode_rub")}
            </button>
            <button type="button" onClick={() => { haptic?.("light"); setMode("crypto"); setAmount(""); }} className={cn("flex-1 py-2 rounded-lg text-sm font-medium transition-colors", mode === "crypto" ? "bg-white dark:bg-gray-700 shadow-sm text-foreground" : "text-muted-foreground")}>
              {t("rubtopup.mode_crypto", { currency })}
            </button>
          </div>

          {/* Сумма */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              {mode === "rub" ? t("rubtopup.amount_rub") : t("rubtopup.amount_crypto", { currency })}
            </label>
            <div className="relative">
              <input
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                className={cn(
                  "w-full bg-gray-50 dark:bg-gray-800 rounded-xl p-4 pr-16 text-lg font-semibold text-foreground outline-none border",
                  rangeError ? "border-red-400" : "border-transparent"
                )}
                data-testid="rubtopup-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">{mode === "rub" ? "₽" : currency}</span>
            </div>

            {presets.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-2" data-testid="rubtopup-presets">
                {presets.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => { haptic?.("light"); setAmount(String(p.value)); }}
                    className={cn(
                      "px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors",
                      Number(amount) === p.value ? "border-primary bg-primary/5 text-foreground" : "border-[rgb(var(--lp-edge)/var(--lp-edge-a))] text-muted-foreground"
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}

            <div className="min-h-[1.25rem] mt-2 text-sm flex items-center gap-2">
              {quoting ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              ) : rangeError ? (
                <span className="text-red-500" data-testid="rubtopup-range-error">
                  {t(`rubtopup.${rangeError.key}`, { amount: rangeError.amount, unit: rangeError.unit })}
                </span>
              ) : null}
            </div>
            {limitHint && (
              <p className="text-xs text-gray-400 mt-1" data-testid="rubtopup-limits">{limitHint}</p>
            )}
          </div>

          {/* Сводка по заявке */}
          {quote && !rangeError && (
            <div className="rounded-2xl bg-gray-50 dark:bg-gray-800/60 divide-y divide-gray-200/70 dark:divide-gray-700/50" data-testid="rubtopup-summary">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("rubtopup.you_receive_label")}</span>
                <span className="text-sm font-semibold text-green-500" data-testid="rubtopup-result">
                  {fmtCrypto(quote.receive_amount, currency)} {currency}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("rubtopup.rate_label")}</span>
                <span className="text-sm font-medium text-foreground">
                  {t("rubtopup.rate_value", { rate: fmtRub(quote.rate_rub_per_unit), currency })}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <button
                  type="button"
                  onClick={() => { haptic?.("light"); toast(t("rubtopup.fee_included_hint")); }}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground"
                >
                  {t("rubtopup.fee_label")}
                  <HelpCircle className="w-3.5 h-3.5" />
                </button>
                <span className="text-sm font-medium text-foreground">{t("rubtopup.fee", { pct: quote.fee_pct })}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">{t("rubtopup.total_label")}</span>
                <span className="text-base font-semibold text-foreground" data-testid="rubtopup-total">
                  {fmtRub(quote.rub_amount)} ₽
                </span>
              </div>
            </div>
          )}

          <button
            onClick={onPrimary}
            disabled={!canProceed}
            className={cn("w-full py-3.5 rounded-xl font-semibold transition-colors", canProceed ? "bg-primary text-primary-foreground" : "bg-gray-200 dark:bg-gray-800 text-gray-400")}
            data-testid="rubtopup-submit"
          >
            {creating ? t("rubtopup.creating") : requiresCustomer ? t("rubtopup.continue") : t("rubtopup.create")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RubTopUpPage;
